import React from "react";
import { FiPhone, FiMail, FiGlobe, FiMapPin } from "react-icons/fi";
import { cn } from "utils/helpers";

type ContactSize = "sm" | "md";

interface ContactItemProps {
	href?: string;
	icon: React.ReactNode;
	isLink?: boolean;
	size?: ContactSize;
	value: string | null | undefined;
}

const sizeClasses: Record<ContactSize, string> = {
	sm: "text-xs",
	md: "text-sm",
};

export const ContactItem: React.FC<ContactItemProps> = ({
	icon,
	value,
	href,
	isLink = false,
	size = "md",
}) => {
	if (!value) {
		return null;
	}

	if (href || isLink) {
		return (
			<a
				href={href}
				target={href?.startsWith("http") ? "_blank" : undefined}
				rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
				className={cn(
					"flex items-start gap-2 text-blue-600 underline decoration-blue-300 underline-offset-2 transition-colors hover:text-blue-800 hover:decoration-blue-600",
					sizeClasses[size]
				)}
			>
				<span className="mt-0.5 shrink-0">{icon}</span>
				<span className="break-all">{value}</span>
			</a>
		);
	}

	return (
		<div className={cn("flex items-start gap-2 text-gray-600", sizeClasses[size])}>
			<span className="mt-0.5 shrink-0 text-gray-400">{icon}</span>
			<span className="break-all">{value}</span>
		</div>
	);
};

interface CompanyContactDetailsProps {
	className?: string;
	email: string | null | undefined;
	phone: string | null | undefined;
	primaryAddress: string | null | undefined;
	showHeading?: boolean;
	size?: ContactSize;
	website: string | null | undefined;
}

const getWebsiteHref = (website: string): string => {
	if (website.startsWith("http")) {
		return website;
	}
	return `https://${website}`;
};

export const CompanyContactDetails: React.FC<CompanyContactDetailsProps> = ({
	className,
	email,
	phone,
	primaryAddress,
	showHeading = true,
	size = "md",
	website,
}) => {
	const iconSize = size === "sm" ? 12 : 14;

	return (
		<div className={cn("flex flex-col gap-2", className)}>
			{showHeading && (
				<h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
					Contact Information
				</h3>
			)}
			<ContactItem
				icon={<FiPhone size={iconSize} />}
				value={phone}
				href={phone ? `tel:${phone}` : undefined}
				isLink
				size={size}
			/>
			<ContactItem
				icon={<FiMail size={iconSize} />}
				value={email}
				href={email ? `mailto:${email}` : undefined}
				isLink
				size={size}
			/>
			<ContactItem
				icon={<FiMapPin size={iconSize} />}
				value={primaryAddress}
				size={size}
			/>
			<ContactItem
				icon={<FiGlobe size={iconSize} />}
				value={website}
				href={website ? getWebsiteHref(website) : undefined}
				isLink
				size={size}
			/>
		</div>
	);
};
